import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
} from "@mui/material";
import { ExpandMore, RestoreOutlined, SearchOff, VisibilityOutlined } from "@mui/icons-material";
import { useRestoreTestStepMutation, useUnhideTestStepMutation } from "../../../useQuery";
import { useNotification } from "../../../hooks";
import { useQueryClient } from "@tanstack/react-query";

const StepAccordion = ({ step, onUnhide, isUnhiding, onRestore, isRestoring }) => {
  const hasExtraDetails = step.Object_Xpath || step.createddate || step.Page_Name || step.subAction;

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        borderTop: "1px solid #e2e8f0",
        borderRadius: "0 !important",
        "&:last-child": { borderBottom: "1px solid #e2e8f0" },
        "&:before": { display: "none" },
        "&.Mui-expanded": { margin: 0 },
      }}
    >
      <AccordionSummary
        expandIcon={hasExtraDetails ? <ExpandMore /> : null}
        sx={{
          minHeight: 56,
          px: 2,
          "&:hover": { backgroundColor: "#f8fafc" },
          "& .MuiAccordionSummary-content": {
            alignItems: "center",
            gap: 2,
            my: 1,
            minWidth: 0,
          },
        }}
      >
        {/* Step Number */}
        <Typography sx={{ width: 70, flexShrink: 0, fontSize: 14 }}>{step.Step_Number}</Typography>

        {/* Type */}
        <Chip
          label={step.stepStatus === "hidden" ? "Hidden" : "Deleted"}
          size="small"
          sx={{
            height: "22px",
            fontSize: "12px",
            backgroundColor: "#f1f5f9",
            color: "#64748b",
            fontWeight: 400,
            width: 60,
          }}
        />

        {/* Action */}
        <Typography sx={{ width: 100, flexShrink: 0, fontSize: 14 }}>
          {step.Action || "-"}
        </Typography>

        {/* Page Description */}
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: 14,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {step.Page_Description || step.nearestname || "-"}
        </Typography>

        {/* Keyword */}
        <Chip
          label={step.Keyword || "-"}
          size="small"
          sx={{
            fontSize: "12px",
            height: "22px",
            width: 80,
            backgroundColor:
              step.Keyword === "Assertion"
                ? "#EFF4FF"
                : step.Keyword === "Inline"
                  ? "#FEF3C7"
                  : "#F3F4F6",
            color:
              step.Keyword === "Assertion"
                ? "#6366f1"
                : step.Keyword === "Inline"
                  ? "#D97706"
                  : "#4B5563",
            fontWeight: 400,
          }}
        />

        {/* Flow */}
        <Chip
          label={step.Flow || "-"}
          size="small"
          sx={{
            fontSize: "12px",
            height: "22px",
            width: 70,
            backgroundColor:
              step.Flow === "Positive"
                ? "#DCFCE7"
                : step.Flow === "Negative"
                  ? "#FEE2E2"
                  : "#FEF3C7",
            color:
              step.Flow === "Positive"
                ? "#166534"
                : step.Flow === "Negative"
                  ? "#991B1B"
                  : "#92400E",
            fontWeight: 400,
          }}
        />

        {/* Action Button - Unhide for hidden, Restore for deleted */}
        <Box sx={{ width: 80, display: "flex", flexShrink: 0 }}>
          {step.stepStatus === "HIDDEN" && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<VisibilityOutlined sx={{ fontSize: 16 }} />}
              disabled={isUnhiding}
              onClick={(e) => {
                e.stopPropagation();
                onUnhide(step);
              }}
              sx={{
                width: "100%",
                textTransform: "none",
                fontSize: "12px",
                height: "28px",
                borderColor: "#e2e8f0",
                color: "#475569",
                "&:hover": {
                  borderColor: "#cbd5e1",
                  backgroundColor: "#f8fafc",
                },
              }}
            >
              Unhide
            </Button>
          )}
          {step.stepStatus === "DELETED" && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<RestoreOutlined sx={{ fontSize: 16 }} />}
              disabled={isRestoring}
              onClick={(e) => {
                e.stopPropagation();
                onRestore(step);
              }}
              sx={{
                width: "100%",
                textTransform: "none",
                fontSize: "12px",
                height: "28px",
                borderColor: "#bbf7d0",
                color: "#166534",
                backgroundColor: "#f0fdf4",
                "&:hover": {
                  borderColor: "#86efac",
                  backgroundColor: "#dcfce7",
                },
              }}
            >
              Restore
            </Button>
          )}
        </Box>
      </AccordionSummary>

      {hasExtraDetails && (
        <AccordionDetails sx={{ backgroundColor: "#f8fafc", px: 3, py: 2 }}>
          <Grid container spacing={2}>
            {step.TestData && (
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">
                  Test Data
                </Typography>
                <Typography variant="body2">{step.TestData}</Typography>
              </Grid>
            )}
            {step.Page_Name && (
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">
                  Page Name
                </Typography>
                <Typography variant="body2">{step.Page_Name}</Typography>
              </Grid>
            )}
            {step.subAction && (
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">
                  Sub Action
                </Typography>
                <Typography variant="body2">{step.subAction}</Typography>
              </Grid>
            )}
            {step.Object_Xpath && (
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">
                  Object Identifier
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "12px",
                    backgroundColor: "#fff",
                    padding: "8px",
                    borderRadius: "4px",
                    border: "1px solid #e2e8f0",
                    wordBreak: "break-all",
                    mt: 0.5,
                  }}
                >
                  {step.Object_Xpath}
                </Typography>
              </Grid>
            )}
            {step.createddate && (
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">
                  Created Date
                </Typography>
                <Typography variant="body2">{step.createddate}</Typography>
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      )}
    </Accordion>
  );
};

const HiddenDeletedStepsModal = ({ open, onClose, data, stepRange }) => {
  const notify = useNotification();
  const queryClient = useQueryClient();
  const { mutate: unhideStep, isPending: isUnhiding } = useUnhideTestStepMutation();
  const { mutate: restoreStep, isPending: isRestoring } = useRestoreTestStepMutation();
  const allSteps = data || [];

  const hiddenCount = allSteps.filter((step) => step.stepStatus === "HIDDEN").length;
  const deletedCount = allSteps.filter((step) => step.stepStatus === "DELETED").length;

  const handleUnhide = (step) => {
    unhideStep(
      { testStepId: step.idtest_step },
      {
        onSuccess: (response) => {
          notify(`Step ${step.Step_Number} has been unhidden successfully`, "success");

          queryClient.invalidateQueries(["latestRecordings"]);

          onClose();
        },
        onError: (error) => {
          console.error("Error unhiding step:", error);
          notify(`Failed to unhide step ${step.Step_Number}`, "error");
        },
      },
    );
  };

  const handleRestore = (step) => {
    restoreStep(
      { testStepId: step.idtest_step },
      {
        onSuccess: () => {
          notify(`Step ${step.Step_Number} has been restored successfully`, "success");
          queryClient.invalidateQueries(["latestRecordings"]);
          onClose();
        },
        onError: (error) => {
          console.error("Error restoring step:", error);
          notify(`Failed to restore step ${step.Step_Number}`, "error");
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          maxHeight: "85vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: "#fff",
          color: "#1e293b",
          borderBottom: "1px solid #e2e6f3",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6" fontWeight={600}>
              Hidden & Deleted Steps
            </Typography>
            {stepRange && (
              <Chip
                label={`Steps ${stepRange[0]} - ${stepRange[1]}`}
                size="small"
                variant="outlined"
                sx={{ ml: 1, borderColor: "#e2e8f0", color: "#64748b" }}
              />
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Chip
              label={`${hiddenCount} Hidden`}
              size="small"
              sx={{
                backgroundColor: "#f1f5f9",
                color: "#64748b",
                fontWeight: 400,
              }}
            />
            <Chip
              label={`${deletedCount} Deleted`}
              size="small"
              sx={{
                backgroundColor: "#f1f5f9",
                color: "#64748b",
                fontWeight: 400,
              }}
            />
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          backgroundColor: "#fff",
          px: 3,
          py: 2,
          overflow: "auto",
          maxHeight: "60vh",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#f1f5f9",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#cbd5e1",
            borderRadius: "4px",
            "&:hover": {
              backgroundColor: "#94a3b8",
            },
          },
        }}
      >
        {allSteps.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              color: "#94a3b8",
            }}
          >
            <SearchOff sx={{ fontSize: 48, mb: 2, color: "#cbd5e1" }} />
            <Typography variant="body1" color="textSecondary">
              No hidden or deleted steps found in the selected range
            </Typography>
            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
              Try selecting a different step range
            </Typography>
          </Box>
        ) : (
          <Box sx={{ pt: 1 }}>
            {/* Header Row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                px: 2,
                py: 1.5,
                mb: 1,
                backgroundColor: "#f8fafc",
                borderBottom: "1px solid #e2e8f0",
              }}
            >
              <Typography
                variant="caption"
                fontWeight={600}
                color="textSecondary"
                sx={{ width: 70, flexShrink: 0 }}
              >
                Step Number
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                color="textSecondary"
                sx={{ width: 60, flexShrink: 0 }}
              >
                Type
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                color="textSecondary"
                sx={{ width: 100, flexShrink: 0 }}
              >
                Action
              </Typography>
              <Typography variant="caption" fontWeight={600} color="textSecondary" sx={{ flex: 1 }}>
                Page Description
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                color="textSecondary"
                sx={{ width: 80, flexShrink: 0 }}
              >
                Keyword
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                color="textSecondary"
                sx={{ width: 70, flexShrink: 0 }}
              >
                Flow
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                color="textSecondary"
                sx={{ width: 80, flexShrink: 0 }}
              >
                Actions
              </Typography>
            </Box>

            {allSteps.map((step, index) => (
              <StepAccordion
                key={`${step.stepStatus}-${step.idtest_step || step.Step_Number || index}`}
                step={step}
                onUnhide={handleUnhide}
                isUnhiding={isUnhiding}
                onRestore={handleRestore}
                isRestoring={isRestoring}
              />
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, backgroundColor: "#fff", borderTop: "1px solid #e2e6f3" }}>
        <Typography variant="caption" color="textSecondary" sx={{ flex: 1 }}>
          Expand for more details
        </Typography>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: "#e2e8f0",
            color: "#475569",
            "&:hover": { borderColor: "#cbd5e1", backgroundColor: "#f8fafc" },
            textTransform: "none",
            px: 3,
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HiddenDeletedStepsModal;
